import type { ApiResponse, RequestConfig } from "./types";
import { buildFetchOptions, buildUrl, parseErrorResponse, parseResponse } from "./helpers";
import { ApiError } from "./types";

const NETWORK_ERROR_MESSAGES = ["failed to fetch", "load failed", "networkerror"];

function isNetworkError(err: TypeError): boolean {
  const msg = err.message.toLowerCase();
  return NETWORK_ERROR_MESSAGES.some((m) => msg.includes(m));
}

type Middleware = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ErrorHandler = (error: ApiError, config: RequestConfig) => any | Promise<any>;

export interface ApiClient {
  <T = any>(urlOrConfig: string | RequestConfig, config?: RequestConfig): Promise<ApiResponse<T>>;
  get: <T = any>(url: string, config?: RequestConfig) => Promise<ApiResponse<T>>;
  post: <T = any>(url: string, data?: any, config?: RequestConfig) => Promise<ApiResponse<T>>;
  put: <T = any>(url: string, data?: any, config?: RequestConfig) => Promise<ApiResponse<T>>;
  patch: <T = any>(url: string, data?: any, config?: RequestConfig) => Promise<ApiResponse<T>>;
  delete: <T = any>(url: string, config?: RequestConfig) => Promise<ApiResponse<T>>;
  defaults: {
    baseURL: string;
    headers: Record<string, string> & { common: Record<string, string> };
  };
  useRequestMiddleware: (mw: Middleware) => void;
  useErrorHandler: (handler: ErrorHandler) => void;
}

export function createApiClient(baseURL: string): ApiClient {
  const defaultHeaders: Record<string, string> = {};
  const requestMiddlewares: Middleware[] = [];
  const errorHandlers: ErrorHandler[] = [];

  async function executeFetch<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    // 1. Run request middlewares
    let processedConfig = { ...config };
    for (const mw of requestMiddlewares) {
      processedConfig = await mw(processedConfig);
    }

    // 2. Build URL with params
    const url = buildUrl(baseURL, processedConfig);

    // 3. Build fetch options
    const fetchOptions = buildFetchOptions(processedConfig, defaultHeaders);

    // 4. Execute fetch
    let response: Response;
    try {
      response = await fetch(url, fetchOptions);
    } catch (err) {
      // AbortError should propagate as-is for isCancelError() detection
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      // "Failed to fetch" / "Load failed" = network error (server down, no connection)
      // "Failed to parse URL" / other TypeError = code bug, propagate as-is
      if (err instanceof TypeError && !isNetworkError(err)) throw err;
      throw new ApiError((err as Error).message || "Network Error", processedConfig);
    }

    // 5. Handle error responses
    if (!response.ok) {
      const errorData = await parseErrorResponse(response);
      const apiError = new ApiError(
        response.statusText || `Request failed with status ${response.status}`,
        processedConfig,
        {
          data: errorData,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        }
      );

      // Run error handlers (for auth refresh, etc.)
      for (const handler of errorHandlers) {
        const result = await handler(apiError, processedConfig);
        if (result !== undefined) return result as ApiResponse<T>;
      }

      throw apiError;
    }

    // 6. Parse success response
    const data = await parseResponse<T>(response, processedConfig);

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: processedConfig,
    };
  }

  // The callable interface - supports api(url, config?) and api(config)
  const apiCallable = function <T = any>(
    urlOrConfig: string | RequestConfig,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    if (typeof urlOrConfig === "string") {
      return executeFetch<T>({ ...config, url: urlOrConfig, method: config?.method || "GET" });
    }
    return executeFetch<T>({ method: "GET", ...urlOrConfig });
  } as ApiClient;

  // Method shortcuts
  apiCallable.get = <T = any>(url: string, config?: RequestConfig) =>
    executeFetch<T>({ ...config, url, method: "GET" });

  apiCallable.post = <T = any>(url: string, data?: any, config?: RequestConfig) =>
    executeFetch<T>({ ...config, url, method: "POST", data });

  apiCallable.put = <T = any>(url: string, data?: any, config?: RequestConfig) =>
    executeFetch<T>({ ...config, url, method: "PUT", data });

  apiCallable.patch = <T = any>(url: string, data?: any, config?: RequestConfig) =>
    executeFetch<T>({ ...config, url, method: "PATCH", data });

  apiCallable.delete = <T = any>(url: string, config?: RequestConfig) =>
    executeFetch<T>({ ...config, url, method: "DELETE" });

  // Proxy-based defaults.headers to support all 4 existing patterns:
  // 1. api.defaults.headers.common["Authorization"] = "..."   (auth.store.ts)
  // 2. api.defaults.headers["Accept-Language"] = "..."         (i18n.ts)
  // 3. api.defaults.headers.Authorization = "..."              (handle-auth.ts)
  // 4. delete api.defaults.headers.common["Authorization"]     (logout)
  const headersProxy = new Proxy(defaultHeaders as Record<string, string> & { common: Record<string, string> }, {
    get(_, prop: string) {
      if (prop === "common") {
        // Return a proxy over the same defaultHeaders so that
        // api.defaults.headers.common["X"] routes to defaultHeaders["X"]
        return new Proxy(defaultHeaders, {
          get(_, innerProp: string) {
            return defaultHeaders[innerProp];
          },
          set(_, innerProp: string, value: string) {
            defaultHeaders[innerProp] = value;
            return true;
          },
          deleteProperty(_, innerProp: string) {
            delete defaultHeaders[innerProp];
            return true;
          },
        });
      }
      return defaultHeaders[prop];
    },
    set(_, prop: string, value: string) {
      if (prop === "common") return true; // Ignore assignment to common itself
      defaultHeaders[prop] = value;
      return true;
    },
    deleteProperty(_, prop: string) {
      delete defaultHeaders[prop];
      return true;
    },
  });

  apiCallable.defaults = {
    baseURL,
    headers: headersProxy,
  };

  apiCallable.useRequestMiddleware = (mw: Middleware) => {
    requestMiddlewares.push(mw);
  };

  apiCallable.useErrorHandler = (handler: ErrorHandler) => {
    errorHandlers.push(handler);
  };

  return apiCallable;
}
